import React, { useState, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { QrCode, Mail, Key, User, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Register = () => {
  const { orgSlug } = useParams(); // Get organization slug from URL
  const [organizationInfo, setOrganizationInfo] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    organization_slug: orgSlug || ''  // Start with empty string, will be set in useEffect
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // MULTI-CLUB: detection of existing account in another club
  const [existingAccountData, setExistingAccountData] = useState(null);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  const { register, isAuthenticated } = useAuth();

  // Load organization info from slug
  useEffect(() => {
    const loadOrganization = async () => {
      if (!orgSlug) {
        setLoadingOrg(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/organizations/by-slug/${orgSlug}`);
        setOrganizationInfo(response.data);
        setFormData(prev => ({
          ...prev,
          organization_slug: orgSlug
        }));
      } catch (err) {
        console.error('Failed to load organization:', err);
        setError(`Kunne ikke finne organisasjonen "${orgSlug}". Kontakt administratoren for riktig registreringslink.`);
      } finally {
        setLoadingOrg(false);
      }
    };

    loadOrganization();
  }, [orgSlug]);

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    // Validate all required fields
    const errors = {};
    
    if (!formData.full_name.trim()) {
      errors.full_name = 'Fullt navn må fylles ut';
    }
    
    // Fødselsdato obligatorisk (DD.MM.YYYY)
    const d = (formData.birth_day || '').padStart(2, '0');
    const m = (formData.birth_month || '').padStart(2, '0');
    const y = formData.birth_year || '';
    const dayNum = parseInt(formData.birth_day, 10);
    const monthNum = parseInt(formData.birth_month, 10);
    const yearNum = parseInt(formData.birth_year, 10);
    const currentYear = new Date().getFullYear();
    if (!formData.birth_day || !formData.birth_month || !formData.birth_year) {
      errors.birth_date = 'Fødselsdato må fylles ut (DD.MM.YYYY)';
    } else if (
      isNaN(dayNum) || dayNum < 1 || dayNum > 31 ||
      isNaN(monthNum) || monthNum < 1 || monthNum > 12 ||
      isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear
    ) {
      errors.birth_date = 'Ugyldig fødselsdato. Bruk format DD.MM.YYYY';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'E-post må fylles ut';
    }
    
    if (!formData.password) {
      errors.password = 'Passord må fylles ut';
    } else if (formData.password.length < 6) {
      errors.password = 'Passord må være minst 6 tegn';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Bekreft passord må fylles ut';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passordene matcher ikke';
    }

    // If there are field errors, show them and stop
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Vennligst fyll ut alle påkrevde felt korrekt');
      setLoading(false);
      return;
    }

    // CRITICAL FIX: Ensure organization_slug is set before registration
    if (!formData.organization_slug) {
      setError('Mangler organisasjon. Vennligst bruk registreringslinken du har fått fra din klubb.');
      setLoading(false);
      return;
    }

    // Remove confirmPassword before sending to backend
    const { confirmPassword, ...registerData } = formData;
    
    // MULTI-CLUB: Check if email exists in another club FIRST
    try {
      const flagsRes = await axios.get(`${API_BASE}/api/feature-flags`);
      if (flagsRes.data?.multi_club_enabled) {
        const checkRes = await axios.post(`${API_BASE}/api/auth/check-email-exists`, {
          email: registerData.email,
          organization_slug: registerData.organization_slug,
        });
        if (checkRes.data?.exists && !checkRes.data?.already_in_this_org) {
          // Show "link existing account" flow instead of normal registration
          setExistingAccountData({
            email: registerData.email,
            password: registerData.password,
            full_name: checkRes.data.full_name || registerData.full_name,
            other_organizations: checkRes.data.other_organizations || [],
            organization_slug: registerData.organization_slug,
            organization_name: organizationInfo?.name || '',
            has_deleted_account: checkRes.data.has_deleted_account === true,
            from_club_name: checkRes.data.from_club_name || null,
          });
          setLoading(false);
          return;
        }
      }
    } catch (e) {
      console.log('Email-exists check failed (continuing with normal register):', e);
    }
    
    const result = await register(registerData);
    
    if (result.success) {
      setSuccess(result.message);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        organization_slug: orgSlug || ''  // Maintain the organization slug from URL
      });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (loadingOrg) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Laster organisasjonsinformasjon...</p>
        </div>
      </div>
    );
  }

  // MULTI-CLUB: Existing account detected -> show "Link existing account" flow
  if (existingAccountData) {
    const isDeletedFlow = existingAccountData.has_deleted_account === true;
    const handleLinkSubmit = async (e) => {
      e.preventDefault();
      setLinkLoading(true);
      setError('');
      try {
        // Deleted-account flow bruker reactivate-cross-club endepunktet som sjekker is_deleted
        const endpoint = isDeletedFlow
          ? `${API_BASE}/api/auth/reactivate-cross-club`
          : `${API_BASE}/api/auth/link-existing-account`;
        const res = await axios.post(endpoint, {
          email: existingAccountData.email,
          password: linkPassword,
          organization_slug: existingAccountData.organization_slug,
        });
        setSuccess(res.data.message || 'Søknad om medlemskap sendt!');
        setExistingAccountData(null);
        setLinkPassword('');
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Kunne ikke knytte konto');
      } finally {
        setLinkLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center ${isDeletedFlow ? 'bg-blue-500' : 'bg-yellow-500'}`}>
              <User className="h-8 w-8 text-gray-900" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-white">
              {isDeletedFlow ? 'Velkommen tilbake!' : 'Vi gjenkjenner deg!'}
            </h2>
            {isDeletedFlow ? (
              <>
                <p className="mt-3 text-gray-300">
                  Hei <span className="text-blue-300 font-semibold">{existingAccountData.full_name}</span>!
                  Vi fant en tidligere konto på denne e-posten som ble slettet i:
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  <span className="px-3 py-1 bg-gray-800 border border-blue-500/40 rounded-full text-sm text-blue-200">
                    {existingAccountData.from_club_name || 'Tidligere klubb'}
                  </span>
                </div>
                <p className="mt-4 text-gray-300 text-sm">
                  Vil du <strong>reaktivere</strong> kontoen din i{' '}
                  <span className="text-white font-semibold">{existingAccountData.organization_name}</span>{' '}
                  og ta med deg hele historikken din (treningsøkter, våpenkort, merker)?
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  Skriv inn passordet du brukte i den gamle klubben for å bekrefte identiteten din.
                </p>
              </>
            ) : (
              <>
                <p className="mt-3 text-gray-300">
                  Hei <span className="text-yellow-400 font-semibold">{existingAccountData.full_name}</span>!
                  Vi ser at du allerede har en Aktivlogg-konto i:
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  {existingAccountData.other_organizations.map((org, i) => (
                    <span key={i} className="px-3 py-1 bg-gray-800 border border-yellow-500/40 rounded-full text-sm text-yellow-300">
                      {org}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-gray-400 text-sm">
                  Vil du knytte din eksisterende konto til{' '}
                  <span className="text-white font-semibold">{existingAccountData.organization_name}</span>?
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg text-sm" data-testid="link-account-error">
              {error}
            </div>
          )}

          <form onSubmit={handleLinkSubmit} className="space-y-4" data-testid="link-existing-form">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Skriv inn ditt eksisterende passord
              </label>
              <input
                type="password"
                value={linkPassword}
                onChange={(e) => setLinkPassword(e.target.value)}
                required
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 focus:border-yellow-500 rounded-lg text-white focus:outline-none"
                placeholder="Passordet du bruker i din andre klubb"
                data-testid="link-account-password"
              />
            </div>
            <button
              type="submit"
              disabled={linkLoading || !linkPassword}
              className={`w-full py-3 font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDeletedFlow ? 'bg-blue-500 text-white hover:bg-blue-400' : 'bg-yellow-500 text-gray-900 hover:bg-yellow-400'}`}
              data-testid="link-account-submit"
            >
              {linkLoading
                ? (isDeletedFlow ? 'Reaktiverer konto...' : 'Sender søknad...')
                : (isDeletedFlow
                    ? `Reaktiver konto i ${existingAccountData.organization_name}`
                    : `Knytt konto til ${existingAccountData.organization_name}`)}
            </button>
            <p className="text-xs text-gray-500 text-center">
              Klubben må godkjenne søknaden før du får tilgang. Du varsles på e-post.
            </p>
            <button
              type="button"
              onClick={() => {
                setExistingAccountData(null);
                setLinkPassword('');
                setError('');
              }}
              className="w-full text-sm text-gray-400 hover:text-yellow-400 transition-colors"
              data-testid="link-account-back"
            >
              ← Tilbake til registrering
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-yellow-500 rounded-full flex items-center justify-center">
            <QrCode className="h-8 w-8 text-gray-900" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-white">
            Bli medlem
          </h2>
          {organizationInfo ? (
            <div className="mt-3">
              <p className="text-xl text-yellow-500 font-semibold">
                {organizationInfo.name}
              </p>
              <p className="mt-1 text-gray-400 text-sm">
                Registrer deg som medlem
              </p>
            </div>
          ) : (
            <p className="mt-2 text-gray-400">
              Registrer deg i Aktivlogg systemet
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-300">
                Fullt navn
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-10 py-3 border placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:z-10 sm:text-sm ${
                    fieldErrors.full_name
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-600 focus:ring-yellow-500 focus:border-yellow-500'
                  }`}
                  placeholder="Fornavn Etternavn"
                />
              </div>
              {fieldErrors.full_name && (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-sm font-medium">
                  <AlertCircle className="h-4 w-4" />
                  <span>{fieldErrors.full_name}</span>
                </div>
              )}
            </div>

            {/* Fødselsdato (obligatorisk - DD.MM.YYYY) */}
            <div>
              <label className="block text-sm font-medium text-gray-300">
                Fødselsdato <span className="text-red-400">*</span>
              </label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                <input
                  type="number"
                  name="birth_day"
                  min="1"
                  max="31"
                  required
                  value={formData.birth_day}
                  onChange={handleChange}
                  placeholder="DD"
                  data-testid="register-birth-day"
                  className={`appearance-none block w-full px-3 py-3 border placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none text-center sm:text-sm ${
                    fieldErrors.birth_date ? 'border-red-500 focus:ring-red-500' : 'border-gray-600 focus:ring-yellow-500 focus:border-yellow-500'
                  }`}
                />
                <input
                  type="number"
                  name="birth_month"
                  min="1"
                  max="12"
                  required
                  value={formData.birth_month}
                  onChange={handleChange}
                  placeholder="MM"
                  data-testid="register-birth-month"
                  className={`appearance-none block w-full px-3 py-3 border placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none text-center sm:text-sm ${
                    fieldErrors.birth_date ? 'border-red-500 focus:ring-red-500' : 'border-gray-600 focus:ring-yellow-500 focus:border-yellow-500'
                  }`}
                />
                <input
                  type="number"
                  name="birth_year"
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                  value={formData.birth_year}
                  onChange={handleChange}
                  placeholder="YYYY"
                  data-testid="register-birth-year"
                  className={`appearance-none block w-full px-3 py-3 border placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none text-center sm:text-sm ${
                    fieldErrors.birth_date ? 'border-red-500 focus:ring-red-500' : 'border-gray-600 focus:ring-yellow-500 focus:border-yellow-500'
                  }`}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Format: DD.MM.YYYY · Brukes for politi-/standard PDF og fødselsdag-påminnelser.</p>
              {fieldErrors.birth_date && (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-sm font-medium">
                  <AlertCircle className="h-4 w-4" />
                  <span>{fieldErrors.birth_date}</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                E-post adresse
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-10 py-3 border placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:z-10 sm:text-sm ${
                    fieldErrors.email
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-600 focus:ring-yellow-500 focus:border-yellow-500'
                  }`}
                  placeholder="din@epost.no"
                />
              </div>
              {fieldErrors.email && (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-sm font-medium">
                  <AlertCircle className="h-4 w-4" />
                  <span>{fieldErrors.email}</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Passord
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-10 pr-10 py-3 border placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:z-10 sm:text-sm ${
                    fieldErrors.password
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-600 focus:ring-yellow-500 focus:border-yellow-500'
                  }`}
                  placeholder="Minst 6 tegn"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-sm font-medium">
                  <AlertCircle className="h-4 w-4" />
                  <span>{fieldErrors.password}</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                Bekreft passord
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-10 pr-10 py-3 border placeholder-gray-400 text-white bg-gray-700 rounded-md focus:outline-none focus:z-10 sm:text-sm ${
                    fieldErrors.confirmPassword
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : formData.confirmPassword && formData.password !== formData.confirmPassword
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : formData.confirmPassword && formData.password === formData.confirmPassword
                      ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                      : 'border-gray-600 focus:ring-yellow-500 focus:border-yellow-500'
                  }`}
                  placeholder="Skriv inn passord på nytt"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
              {/* Password match indicator or error */}
              {fieldErrors.confirmPassword ? (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-sm font-medium">
                  <AlertCircle className="h-4 w-4" />
                  <span>{fieldErrors.confirmPassword}</span>
                </div>
              ) : formData.confirmPassword && (
                <div className="mt-2">
                  {formData.password !== formData.confirmPassword ? (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-medium">Passordene matcher ikke!</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                      <span className="font-medium">Passordene matcher ✓</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-500 border-2 border-red-600 rounded-lg p-4 shadow-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-white flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-semibold text-base">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-600/20 border border-green-600/50 rounded-md p-4">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-green-400 font-medium mb-2">Registrering vellykket! 🎉</p>
                  <p className="text-green-300 text-sm leading-relaxed">
                    Vi har sendt en e-post til <strong>{formData.email}</strong> med en verifiseringslenke. 
                    Klikk på lenken i e-posten for å bekrefte din e-postadresse.
                  </p>
                  <p className="text-green-300 text-sm mt-2">
                    📧 Sjekk også spam/søppelpost-mappen hvis du ikke ser e-posten.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Registrerer...' : 'Registrer meg'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-gray-400 text-sm">
              Har du allerede konto?{' '}
              <Link to="/login" className="text-yellow-500 hover:text-yellow-400">
                Logg inn her
              </Link>
            </p>
          </div>

          <div className="bg-blue-600/20 border border-blue-600/50 rounded-md p-4">
            <h3 className="text-blue-400 font-medium mb-2">Viktig informasjon:</h3>
            <ul className="text-blue-300 text-sm space-y-1">
              <li>• Kontoen må godkjennes av en administrator</li>
              <li>• Du vil få e-post når kontoen er aktivert</li>
              {organizationInfo && (
                <li>• Registrering er for <strong>{organizationInfo.name}</strong></li>
              )}
            </ul>
          </div>

          {!orgSlug && (
            <div className="bg-yellow-600/20 border border-yellow-600/50 rounded-md p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-yellow-400 font-medium mb-1">OBS: Mangler organisasjonslink</h3>
                  <p className="text-yellow-300 text-sm">
                    Du må bruke registreringslinken du har fått fra din klubb. Kontakt skytelederen din for riktig link.
                  </p>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Register;